import { z } from "zod";

/**
 * Phase 8b — wire schema for R-AI-1 panel-scan responses. The AI
 * proposes either panel-level metadata (manufacturer / model / main
 * amperage) or a per-tile breaker read (one suggestion per breaker
 * device the model sees in the photos). One `SuggestionSet` carries an
 * arbitrary mix — typically one `panel-metadata` proposal plus
 * `breaker-read` proposals in panel-position reading order.
 *
 * Schema content is intentionally *only* what 8b's seam needs to
 * validate. The apply-to-panel mapping (Phase 8c) consumes these
 * proposals and routes them through the `panel` / `breaker` /
 * `installBreaker` smart factories, where the full set of domain
 * invariants (placement, conflict, half-form pole rules, …) get
 * re-checked. 8b is responsible only for "what shape did the AI
 * send?", not "is the resulting panel legal?".
 */

const positionProposalSchema = z.object({
  row: z.number().int().nonnegative(),
  column: z.number().int().nonnegative(),
});

const quadConfigProposalSchema = z.enum([
  "two-double",
  "double-plus-two-single",
  "four-single",
]);

const poleProposalSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("single") }),
  z.object({ kind: z.literal("double") }),
  z.object({ kind: z.literal("triple") }),
  z.object({ kind: z.literal("tandem") }),
  z.object({ kind: z.literal("quad"), config: quadConfigProposalSchema }),
]);

const subSlotProposalSchema = z.enum(["upper", "lower"]);

const formFactorProposalSchema = z.enum(["full", "half"]);

const protectionTypeProposalSchema = z.enum([
  "GFCI",
  "AFCI",
  "DFCI",
  "HACR",
  "ShuntTrip",
  "SWD",
  "HID",
]);

const circuitProposalSchema = z.object({
  amperage: z.number().int().positive(),
  shortDescription: z.string().optional(),
});

/**
 * Per-tile breaker read. The model emits one of these for each
 * physical breaker device it sees. `position` is the tile the head of
 * the breaker occupies; multi-pole continuations are inferred at apply
 * time from `pole` (which spells the consumed-position count) rather
 * than being emitted as separate suggestions — the AI reports devices,
 * not cells, mirroring the domain's `BreakerInstallation` aggregate.
 *
 * `circuits` is an ordered list (min 1) whose length is expected to
 * match the breaker's `circuitsCarried` (1 for single/double/triple, 2
 * for tandem, 2–4 for quad by config). Each entry carries the
 * per-circuit `amperage` (the rating now lives on the circuit, not the
 * device — Phase 15a) and an optional `shortDescription`. 8b does not
 * validate the length against the pole — that's an apply-time concern;
 * the schema accepts any length ≥ 1 and 8c reconciles against the pole
 * at the seam to the panel (padding a missing trailing amperage).
 */
const breakerReadProposalSchema = z.object({
  kind: z.literal("breaker-read"),
  position: positionProposalSchema,
  subSlot: subSlotProposalSchema.optional(),
  formFactor: formFactorProposalSchema.optional(),
  pole: poleProposalSchema,
  protectionTypes: z.array(protectionTypeProposalSchema).optional(),
  circuits: z.array(circuitProposalSchema).min(1),
});

/**
 * Numbering schemes the AI may propose. Mirrors the non-custom arms
 * of the domain `NumberingScheme` discriminated union; the `custom`
 * arm is intentionally excluded because a custom mapping requires
 * positional click-order from the user and is not something the AI
 * can recover from a photo.
 */
const numberingSchemeProposalSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("sequential-down-then-over") }),
  z.object({ kind: z.literal("odd-left-even-right") }),
  z.object({ kind: z.literal("sequential-left-then-down") }),
]);

/**
 * Panel-level metadata. Every field is optional because the AI may
 * recover any subset (a closer-cropped two-shot may identify model but
 * miss the main-amperage label, for instance). An object with every
 * field undefined is structurally legal — the apply step skips it.
 *
 * `series` is the manufacturer's product family (e.g., `QO Series`,
 * `CH`, `Homeline`) — surfaced in the mockup's confirm screen
 * alongside manufacturer and model. Not a field on `Panel` today;
 * the apply step uses it as a lookup hint against the panel-model
 * catalog rather than persisting it directly.
 *
 * `totalSlots` and `numberingScheme` are layout-shape proposals so
 * the apply step can construct a `PanelLayout` without separately
 * asking the user. Both are optional — the catalog default for a
 * known model is preferred when set.
 */
const panelMetadataProposalSchema = z.object({
  kind: z.literal("panel-metadata"),
  manufacturer: z.string().min(1).optional(),
  series: z.string().min(1).optional(),
  model: z.string().min(1).optional(),
  mainAmperage: z.number().int().positive().optional(),
  totalSlots: z.number().int().positive().max(84).optional(),
  numberingScheme: numberingSchemeProposalSchema.optional(),
});

export const panelScanProposalSchema = z.discriminatedUnion("kind", [
  panelMetadataProposalSchema,
  breakerReadProposalSchema,
]);

export type PanelScanProposal = z.infer<typeof panelScanProposalSchema>;
export type PanelMetadataProposal = z.infer<typeof panelMetadataProposalSchema>;
export type BreakerReadProposal = z.infer<typeof breakerReadProposalSchema>;
export type CircuitProposal = z.infer<typeof circuitProposalSchema>;
